// src/lib/rotations.ts
// Types et helpers partagés : gestion live (RotationManager), stats et planificateur.

// --- Types ---
export type RPlayer = {
  number: number;
  firstName: string;
  lastName: string;
  position?: string;
};

export type Sub = {
  id: string;
  period: number;
  clockMs: number;
  inNum: number | null;
  outNum: number | null;
};

export type Settings = {
  periods: number;
  periodMinutes: number;
  otMinutes: number;
};

export type RotationState = {
  version: 1;
  squad: RPlayer[];
  settings: Settings;
  hasStarted: boolean;
  starters: (number | null)[];
  onCourt: (number | null)[];
  period: number;
  clockMs: number;
  // joueur -> période -> ms joués
  playMs: Record<number, Record<number, number>>;
  // temps passé sur le terrain depuis la dernière entrée
  stintMs: Record<number, number>;
  subs: Sub[];
  updatedAt: number;
};

export type Snapshot = Pick<RotationState, "onCourt" | "subs" | "starters" | "hasStarted">;

export const COURT_SIZE = 5;
export const DEFAULT_SETTINGS: Settings = { periods: 4, periodMinutes: 10, otMinutes: 5 };

export const storageKey = (matchId: string) => `swish:rotations:${matchId}`;

export const periodDurationMs = (s: Settings, period: number) =>
  (period <= s.periods ? s.periodMinutes : s.otMinutes) * 60_000;

export const emptyState = (squad: RPlayer[], settings = DEFAULT_SETTINGS): RotationState => ({
  version: 1,
  squad,
  settings,
  hasStarted: false,
  starters: Array(COURT_SIZE).fill(null),
  onCourt: Array(COURT_SIZE).fill(null),
  period: 1,
  clockMs: periodDurationMs(settings, 1),
  playMs: {},
  stintMs: {},
  subs: [],
  updatedAt: Date.now(),
});

export const isValidState = (x: any): x is RotationState =>
  x && x.version === 1 && Array.isArray(x.squad) && Array.isArray(x.onCourt);

export const formatClock = (ms: number) => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

export const formatPlay = (ms: number) => {
  const total = Math.floor(Math.max(0, ms) / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

export const parseClock = (value: string): number | null => {
  const m = value.trim().match(/^(\d{1,2})(?::(\d{1,2}))?$/);
  if (!m) return null;
  const min = parseInt(m[1], 10);
  const sec = m[2] ? parseInt(m[2], 10) : 0;
  if (sec > 59) return null;
  return (min * 60 + sec) * 1000;
};

export const totalPlay = (st: RotationState, num: number) =>
  Object.values(st.playMs[num] || {}).reduce((a, b) => a + b, 0);

export const normalizePlayers = (raw: unknown): RPlayer[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((p: any) => p && typeof p.number === "number")
    .map((p: any) => ({
      number: p.number,
      firstName: p.firstName ?? "",
      lastName: p.lastName ?? "",
      position: p.position ?? "",
    }));
};

export const mergeByNumber = (...lists: RPlayer[][]) => {
  const map = new Map<number, RPlayer>();
  lists.forEach((l) => l.forEach((p) => map.set(p.number, p)));
  return Array.from(map.values()).sort((a, b) => a.number - b.number);
};


// ======================================================================
// Plans de rotation (préparés à l'avance) — table public.rotation_plans
// ======================================================================

export type PlanPlayer = RPlayer & { positions: number[] };

export type PlanSegment = {
  id: string;
  period: number;
  lane: number; // 0..4 (MEN..PIV)
  player: number;
  start: number; // secondes écoulées depuis le début de la période
  end: number;
};

export type PlanData = {
  version: 1;
  settings: Pick<Settings, "periods" | "periodMinutes">;
  players: PlanPlayer[];
  segments: PlanSegment[];
};

export type RotationPlanRow = {
  id: string;
  user_id: string;
  team_id: string | null;
  name: string;
  data: PlanData;
  created_at: string;
  updated_at: string;
};

export const emptyPlan = (players: RPlayer[] = []): PlanData => ({
  version: 1,
  settings: { periods: DEFAULT_SETTINGS.periods, periodMinutes: DEFAULT_SETTINGS.periodMinutes },
  players: players.map((p) => ({ ...p, positions: [] })),
  segments: [],
});

export const isValidPlan = (x: any): x is PlanData =>
  x && x.version === 1 && Array.isArray(x.players) && Array.isArray(x.segments);

export const newId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// Palette des cartes joueurs (même ordre que la liste du plan).
export const PLAYER_COLORS = [
  // Pas de verts/turquoises : réservés aux indicateurs de validation.
  "#f97316", "#3b82f6", "#ec4899", "#eab308", "#8b5cf6",
  "#ef4444", "#1e3a8a", "#d946ef", "#b45309", "#64748b",
  "#f43f5e", "#6366f1", "#9a3412", "#be185d", "#0f172a",
];

export const formatSeconds = (sec: number) => formatPlay(sec * 1000);

// ======================================================================
// Stats de temps de jeu sur plusieurs matchs (depuis matches.rotations)
// ======================================================================

export type PlayerMinutesAgg = {
  number: number;
  games: number; // matchs avec du temps de jeu
  totalMs: number;
  // période -> { ms cumulés, nb de matchs où il a joué cette période }
  byPeriod: Record<number, { ms: number; games: number }>;
};

export const aggregateMinutes = (states: RotationState[]) => {
  const map = new Map<number, PlayerMinutesAgg>();
  states.forEach((st) => {
    Object.entries(st.playMs || {}).forEach(([numStr, periods]) => {
      const num = Number(numStr);
      const total = Object.values(periods || {}).reduce((a, b) => a + b, 0);
      if (total <= 0) return;
      const agg =
        map.get(num) ?? { number: num, games: 0, totalMs: 0, byPeriod: {} as PlayerMinutesAgg["byPeriod"] };
      agg.games += 1;
      agg.totalMs += total;
      Object.entries(periods || {}).forEach(([pStr, ms]) => {
        if (!ms) return;
        const p = Number(pStr);
        const cur = agg.byPeriod[p] ?? { ms: 0, games: 0 };
        agg.byPeriod[p] = { ms: cur.ms + ms, games: cur.games + 1 };
      });
      map.set(num, agg);
    });
  });
  return Array.from(map.values()).sort((a, b) => b.totalMs - a.totalMs);
};

// Retourne l'état le plus récent entre la sauvegarde distante et le brouillon local.
export const bestRotationState = (matchId: string, remote: unknown): RotationState | null => {
  let local: RotationState | null = null;
  try {
    const raw = localStorage.getItem(storageKey(matchId));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (isValidState(parsed)) local = parsed;
    }
  } catch {
    /* stockage indisponible */
  }
  const rem = isValidState(remote) ? remote : null;
  if (local && rem) return local.updatedAt >= rem.updatedAt ? local : rem;
  return local ?? rem;
};
